import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Building2, Search, Plus, X, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import { DuplicateContactError, throwContactWriteError, toastDuplicateContact } from "@/lib/duplicateContact";

interface AddContactDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preselectedCompany?: { id: string; name: string; category: string } | null;
}

interface Company {
  id: string;
  name: string;
  category: string;
  subType?: string;
}

const industries = ["Healthcare", "Technology", "Manufacturing", "Business Services", "Consumer", "Energy", "Industrial", "Financial Services"];

const getCategoryBadgeStyle = (category: string) => {
  switch (category) {
    case "Buyer": return "bg-secondary text-secondary-foreground";
    case "Client": return "gold-gradient text-primary-foreground";
    case "COI": return "bg-transparent border-2 border-secondary text-secondary";
    default: return "bg-muted text-muted-foreground";
  }
};

export function AddContactDrawer({ open, onOpenChange, preselectedCompany }: AddContactDrawerProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(
    preselectedCompany ? { id: preselectedCompany.id, name: preselectedCompany.name, category: preselectedCompany.category } : null
  );
  const [showCreateCompany, setShowCreateCompany] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [title, setTitle] = useState("");
  const [linkedIn, setLinkedIn] = useState("");
  const [isPrimary, setIsPrimary] = useState(false);
  const [notes, setNotes] = useState("");

  const [newCompanyCategory, setNewCompanyCategory] = useState<string | null>(null);
  const [newCompanyName, setNewCompanyName] = useState("");
  const [newCompanyIndustry, setNewCompanyIndustry] = useState("");
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();
  const { toast } = useToast();

  const { data: companies = [] } = useQuery<Company[]>({
    queryKey: ["companies"],
    queryFn: async () => {
      const res = await fetch("/api/companies");
      if (!res.ok) throw new Error("Failed to fetch companies");
      return res.json();
    },
  });

  const createContactMutation = useMutation({
    mutationFn: async (data: {
      companyId: string;
      firstName: string;
      lastName: string;
      email: string;
      phone?: string;
      title?: string;
      linkedinUrl?: string;
      isPrimary?: boolean;
      notes?: string;
    }) => {
      const res = await fetch("/api/contacts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        await throwContactWriteError(res, "Failed to create contact");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      queryClient.invalidateQueries({ queryKey: ["companies"] });
      toast({ title: "Contact created", description: "The contact has been added successfully." });
      handleClose();
    },
    onError: (error: Error) => {
      if (error instanceof DuplicateContactError) return toastDuplicateContact(error.body, navigate);
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const filteredCompanies = companies.filter((c: Company) => 
    c.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleClose = () => {
    setSearchQuery("");
    setSelectedCompany(preselectedCompany ? { id: preselectedCompany.id, name: preselectedCompany.name, category: preselectedCompany.category } : null);
    setShowCreateCompany(false);
    setFirstName("");
    setLastName("");
    setEmail("");
    setPhone("");
    setTitle("");
    setLinkedIn("");
    setIsPrimary(false);
    setNotes("");
    setNewCompanyCategory(null);
    setNewCompanyName("");
    setNewCompanyIndustry("");
    onOpenChange(false);
  };

  const handleSave = () => {
    const companyId = selectedCompany?.id || preselectedCompany?.id;
    if (!companyId) {
      toast({ title: "Missing fields", description: "Please select a company.", variant: "destructive" });
      return;
    }
    if (!firstName.trim() || !lastName.trim()) {
      toast({ title: "Missing fields", description: "Please enter the contact's first and last name.", variant: "destructive" });
      return;
    }
    if (!email.trim()) {
      toast({ title: "Missing fields", description: "Please enter the contact's email address.", variant: "destructive" });
      return;
    }

    createContactMutation.mutate({
      companyId,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      phone: phone.trim() || undefined,
      title: title.trim(),
      linkedinUrl: linkedIn.trim() || undefined,
      isPrimary,
      notes: notes.trim() || undefined,
    });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[480px] sm:max-w-[480px] p-0 flex flex-col" data-testid="add-contact-drawer">
        <SheetHeader className="px-6 py-5 border-b border-border flex-shrink-0">
          <SheetTitle className="text-xl font-semibold">Add Contact</SheetTitle>
        </SheetHeader>
        
        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Company <span className="text-red-500">*</span>
              </Label>
              <p className="text-sm text-muted-foreground mt-1">Every contact must belong to a company.</p>
            </div>

            {preselectedCompany ? (
              <div className="p-4 rounded-lg border border-border bg-muted/30">
                <p className="text-xs text-muted-foreground mb-2">Adding contact to:</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-secondary-foreground" />
                  </div>
                  <div>
                    <p className="font-semibold">{preselectedCompany.name}</p>
                    <Badge className={cn("text-[10px]", getCategoryBadgeStyle(preselectedCompany.category))}>
                      {preselectedCompany.category}
                    </Badge>
                  </div>
                </div>
              </div>
            ) : selectedCompany ? (
              <div className="p-4 rounded-lg border border-border bg-muted/30">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-secondary-foreground" />
                    </div>
                    <div>
                      <p className="font-semibold">{selectedCompany.name}</p>
                      <Badge className={cn("text-[10px]", getCategoryBadgeStyle(selectedCompany.category))}>
                        {selectedCompany.category}
                      </Badge>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedCompany(null)} className="text-primary-text">
                    Change
                  </Button>
                </div>
              </div>
            ) : !showCreateCompany ? (
              <Command className="rounded-lg border border-border">
                <div className="flex items-center border-b px-3">
                  <Search className="w-4 h-4 text-muted-foreground mr-2" />
                  <CommandInput 
                    placeholder="Search for company..." 
                    value={searchQuery}
                    onValueChange={setSearchQuery}
                    className="border-0 focus:ring-0"
                  />
                </div>
                <CommandList className="max-h-[200px]">
                  <CommandEmpty>No companies found.</CommandEmpty>
                  <CommandGroup heading="Matching Companies">
                    {filteredCompanies.map((company) => (
                      <CommandItem
                        key={company.id}
                        onSelect={() => setSelectedCompany(company)}
                        className="p-3 cursor-pointer"
                      >
                        <div className="flex items-center gap-3 w-full">
                          <div className="w-9 h-9 rounded-lg bg-secondary/50 flex items-center justify-center">
                            <Building2 className="w-4 h-4 text-secondary-foreground" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{company.name}</span>
                              <Badge className={cn("text-[10px]", getCategoryBadgeStyle(company.category))}>
                                {company.category}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                  <CommandSeparator />
                  <CommandGroup>
                    <CommandItem onSelect={() => setShowCreateCompany(true)} className="p-3 cursor-pointer">
                      <Plus className="w-4 h-4 mr-2 text-primary-text" />
                      <span className="text-primary-text font-medium">Create new company</span>
                    </CommandItem>
                  </CommandGroup>
                </CommandList>
              </Command>
            ) : (
              <div className="p-4 rounded-lg border border-border bg-muted/30 space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Create New Company</p>
                  <Button variant="ghost" size="sm" onClick={() => setShowCreateCompany(false)}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                
                <div className="flex gap-2">
                  {["Buyer", "Client", "COI"].map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setNewCompanyCategory(cat)}
                      className={cn(
                        "flex-1 py-2 px-3 rounded-lg text-xs font-medium border-2 transition-all",
                        newCompanyCategory === cat 
                          ? "gold-gradient border-transparent text-white" 
                          : "border-border hover:border-primary"
                      )}
                    >
                      {cat}
                    </button>
                  ))}
                </div>

                <div className="space-y-2">
                  <Label className="text-sm">Company Name <span className="text-red-500">*</span></Label>
                  <Input
                    value={newCompanyName}
                    onChange={(e) => setNewCompanyName(e.target.value)}
                    className="h-10"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm">Industry <span className="text-red-500">*</span></Label>
                  <Select value={newCompanyIndustry} onValueChange={setNewCompanyIndustry}>
                    <SelectTrigger className="h-10">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      {industries.map((ind) => (
                        <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>

          {(selectedCompany || preselectedCompany || showCreateCompany) && (
            <div className="space-y-5 animate-fade-in pt-4 border-t border-border">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground block">
                Contact Information
              </Label>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>First Name <span className="text-red-500">*</span></Label>
                  <Input
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="h-11"
                    data-testid="input-contact-first-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Last Name <span className="text-red-500">*</span></Label>
                  <Input
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="h-11"
                    data-testid="input-contact-last-name"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Email <span className="text-red-500">*</span></Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-11"
                  data-testid="input-contact-email"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="h-11"
                    data-testid="input-contact-phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Title</Label>
                  <Input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g., Managing Partner"
                    className="h-11"
                    data-testid="input-contact-title"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>LinkedIn</Label>
                <Input
                  value={linkedIn}
                  onChange={(e) => setLinkedIn(e.target.value)}
                  placeholder="https://linkedin.com/in/"
                  className="h-11"
                  data-testid="input-contact-linkedin"
                />
              </div>

              <div className="flex items-center space-x-2 py-2">
                <Checkbox 
                  id="primary" 
                  checked={isPrimary} 
                  onCheckedChange={(checked) => setIsPrimary(checked as boolean)}
                  className="border-primary data-[state=checked]:bg-primary"
                />
                <Label htmlFor="primary" className="font-normal cursor-pointer">
                  Set as primary contact for this company
                </Label>
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="min-h-[80px] resize-none"
                  data-testid="input-contact-notes"
                />
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-border bg-muted/30 flex-shrink-0 flex items-center justify-end gap-3">
          <Button variant="ghost" onClick={handleClose} disabled={createContactMutation.isPending} data-testid="btn-contact-cancel">
            Cancel
          </Button>
          <Button 
            onClick={handleSave}
            disabled={createContactMutation.isPending || (!selectedCompany && !preselectedCompany) || !firstName.trim() || !lastName.trim() || !email.trim()}
            className="gold-gradient text-primary-foreground font-semibold btn-gold" 
            data-testid="btn-contact-save"
          >
            {createContactMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
